/**
 * Microphone capture, VAD and energy envelope.
 *
 * The awkward part of doing this in a browser is §10.2: `echoCancellation`,
 * `noiseSuppression` and `autoGainControl` are requested off, but browsers
 * partially ignore that, and [M] aggressive AGC and compression are exactly what
 * destroyed attribution in testing -- 43% of analysis windows under threshold and
 * 212 s of one speaker's speech lost. So `probeCapabilities` reports what the
 * browser *claims* it turned off, and the app surfaces it rather than hiding it.
 *
 * What `getSettings()` reports is not what the OS does. An empirical gain-riding
 * test -- emit a steady tone and watch whether captured level drifts -- would be
 * the stronger probe, and is not implemented here.
 */

import * as K from "./const.js";

// Date.now() is quantised to at least 1 ms in the browser environments we support.
// Keep the wire uncertainty conservative enough to cover that timer resolution as
// well as one canonical sample's quantisation.
const CAPTURE_TIMER_RESOLUTION_MS = 1;

/** Request the mic with all processing off, at 16 kHz mono if the browser allows. */
export async function openMic() {
  const constraints = {
    audio: {
      channelCount: 1,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      sampleRate: K.SAMPLE_RATE,
    },
    video: false,
  };
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  return stream;
}

/** What the browser says it honoured. Recorded per device on the server (§10.2). */
export function probeCapabilities(stream, ctx) {
  const track = stream.getAudioTracks()[0];
  const s = track ? track.getSettings() : {};
  return {
    sample_rate: ctx ? ctx.sampleRate : s.sampleRate ?? null,
    // `undefined` means the browser did not report the flag at all, which is
    // different from reporting it as off. Keep the distinction.
    agc_reported: s.autoGainControl ?? null,
    ns_reported: s.noiseSuppression ?? null,
    aec_reported: s.echoCancellation ?? null,
    platform: navigator.platform || null,
    user_agent: navigator.userAgent.slice(0, 500),
  };
}

/**
 * Describe the capture health in words a participant can act on.
 * Returns [] when everything is as requested.
 */
export function captureWarnings(caps) {
  const out = [];
  if (caps.agc_reported === true) {
    out.push(
      "This browser kept automatic gain control on. [M] AGC and codec processing " +
        "put 43% of analysis windows below threshold in testing and lost 212 s of " +
        "one speaker. Attribution will be worse than it should be."
    );
  }
  if (caps.ns_reported === true) {
    out.push("Noise suppression is on and could not be disabled; it alters the voice the template was built from.");
  }
  if (caps.aec_reported === true) {
    out.push("Echo cancellation is on and could not be disabled.");
  }
  if (caps.sample_rate && Math.abs(caps.sample_rate - K.SAMPLE_RATE) > 1) {
    out.push(`Capture runs at ${caps.sample_rate} Hz and is resampled to ${K.SAMPLE_RATE} Hz.`);
  }
  return out;
}

/**
 * Continuous recorder producing 16 kHz mono Float32.
 *
 * An AudioWorklet rather than ScriptProcessor: capture must not glitch while the
 * main thread is running a 2 s embedding, and ScriptProcessor runs on the main
 * thread by design.
 */
/**
 * Loudest absolute sample in a frame buffer, carrying a running maximum.
 *
 * Its own function so a test can run the real thing. It is three lines and it lives on the
 * capture path, which is exactly the combination that makes a restated copy in a test pass
 * while the shipped loop is wrong -- and a wrong loop here is not an error, it is a level
 * meter that sits at zero all session.
 *
 * `Math.abs` avoided deliberately: 125 calls a second over 128 samples each.
 */
export function framePeak(frames, from = 0) {
  let peak = from;
  for (let i = 0; i < frames.length; i++) {
    const v = frames[i] < 0 ? -frames[i] : frames[i];
    if (v > peak) peak = v;
  }
  return peak;
}

/**
 * Was the microphone suspended over this interval, or is the clock just slow?
 *
 * Its own function so the rule can be asserted without a microphone, an AudioContext or a
 * DOM -- the same reason `engine/wrapup.ts` and `routes/guard-policy.ts` are their own
 * modules, and it earns it the same way they did. This rule shipped once as a comparison
 * nothing could see, held an assumption ([A] 100 ppm) that reality misses by 390x, and cost
 * every multi-device session its attribution before anybody could tell it was the cause.
 *
 * This answers only "should the participant be told the microphone stopped?" -- the
 * shortfall is padded either way, because keeping buffer time on the wall clock is what
 * lets §5 place two devices with one scalar offset. See `reconcile`.
 *
 * Two tests, and they reject different things:
 *
 * - **Absolute.** Below `CAPTURE_GAP_MIN_SEC` a hole is not worth putting in the timeline
 *   whatever caused it -- startup latency, buffering, a scheduling hiccup.
 * - **Proportional.** Above it, the question is whether the microphone delivered anything.
 *   Drift takes a fixed small fraction of any interval; a suspension takes nearly all of
 *   it. Without this test a background tick throttled to 30 s apart reports 1.17 s of
 *   perfectly ordinary drift as a capture failure.
 *
 * @param {number} missingSec  wall time minus audio delivered, over one interval
 * @param {number} intervalSec wall time the interval covered
 */
export function isCaptureStop(missingSec, intervalSec) {
  if (!(intervalSec > 0)) return false;
  if (missingSec < K.CAPTURE_GAP_MIN_SEC) return false;
  return missingSec >= intervalSec * K.CAPTURE_STOP_FRACTION;
}

export class Recorder {
  constructor() {
    this.ctx = null;
    this.stream = null;
    this.node = null;
    this.chunks = [];
    this.length = 0;
    //: The context's real sample rate, cached so diagnostics can report it after stop.
    //: Audio chunks are canonicalized into `chunks` at `timelineRate` before consumers
    //: can read them; `rate` is retained as the source/capture rate.
    this.rate = 0;
    this.timelineRate = K.SAMPLE_RATE;
    this.canonicalizer = null;
    this.startedAt = null;
    //: Settles on the first frame the worklet delivers; set by `start`.
    this.firstAudio = null;
    //: Monotonic reference for `reconcile`. `performance.now()` rather than
    //: `Date.now()`, which an NTP correction mid-session can move sideways.
    this.startedAtPerf = null;
    //: `ctx.currentTime` when capture began. The context clock does not start at
    //: zero, and it is the clock `reconcile` measures against.
    this.ctxStartTime = 0;
    //: Silence already inserted. A record now rather than an input: `reconcile` works on
    //: per-interval deltas, and padding moves neither clock it reads.
    this.paddedSec = 0;
    //: The padded part of `length`, in samples. The delivery watchdog compares the
    //: context clock against audio actually delivered, so padding must not count
    //: as delivery; this is the exact count to subtract rather than a
    //: seconds round-trip that drifts a frame per pad.
    this.paddedSamples = 0;
    //: The previous pair of clock readings, because what `reconcile` asks is how far the
    //: two moved *apart since the last check* -- see there for why the total is useless.
    this.lastWall = 0;
    this.lastAudio = 0;
    //: Delivered-audio cursor at the last check, in canonical seconds net of
    //: padding: the third reading the delivery watchdog reconciles.
    this.lastDelivered = 0;
    //: An unconfirmed delivery stall, or null. One suspicious tick arms it; the
    //: next tick confirms (padding the whole span) or clears it (the backlog
    //: flushed). See `reconcile`.
    this.suspect = null;
    //: Stretches where the microphone delivered nothing. See `reconcile`.
    this.gaps = [];
    //: Loudest sample since the level meter last read it. See `takePeak`.
    this.peak = 0;
    //: Number of AudioWorklet messages received; metadata-only capture evidence.
    this.workletMessages = 0;
  }

  /**
   * Loudest sample since the last call, then reset. For the level meter.
   *
   * Maintained in the message handler rather than computed on demand because `slice`
   * walks the chunk list from the start on every call, and `chunks` grows by 125 entries
   * a second: the meter's old `slice(elapsed - 0.2, elapsed)` was O(chunks) per paint,
   * which is affordable twice a second and not affordable per frame. Here the samples are
   * already in hand.
   *
   * Resetting on read means one reader. A second one would see whatever the first left.
   */
  takePeak() {
    const peak = this.peak;
    this.peak = 0;
    return peak;
  }

  /**
   * Detect a suspended microphone and keep the timeline honest about it.
   *
   * A phone that locks its screen stops delivering frames. The damage is not the
   * lost audio -- it is that the buffer then *concatenates* across the hole, so
   * everything after it sits earlier in this device's timeline than it happened.
   * §5 aligns devices with a single scalar offset and a ±2 s search, and no single
   * offset fits a timeline that has had 30 s excised from the middle. So one
   * pocketed phone does not merely lose its own speech: alignment is rejected and
   * that participant drops out of the merge entirely. (The field session where
   * `dev_ef336f3b` locked at 1/4 probes has this shape, though nothing proves that
   * is what happened to it.)
   *
   * Padding the hole with silence costs the speech that happened during it -- that
   * is gone either way -- and keeps every later timestamp true, which turns a total
   * loss into a partial one. VAD reads the padding as silence, so nothing is
   * claimed across it.
   *
   * **The comparison is against the context's clock, not against frames received,
   * and the difference is not subtle.** Frames arrive through `port.onmessage`,
   * which runs on the main thread -- the same thread that blocks for seconds while
   * ECAPA embeds a chunk. Measuring frames-received against the wall clock therefore
   * reports every scoring pass as a suspended microphone: it fabricated a 5.7 s gap
   * at exactly the end of the first chunk in two real sessions, padded silence over
   * audio still sitting in the message queue, and printed a caveat blaming the
   * participant's device. `ctx.currentTime` keeps advancing while the main thread is
   * busy and freezes only when the context genuinely is not running, which is the
   * question actually being asked.
   */
  reconcile() {
    if (!this.ctx || this.startedAtPerf === null) return null;
    const wall = (performance.now() - this.startedAtPerf) / 1000;
    const audioElapsed = this.ctx.currentTime - this.ctxStartTime;

    // Per interval, not since the start of the session.
    //
    // [M] The totals version of this shipped and padded silence into recordings that had
    // no hole in them. `wall - audioElapsed` grows on its own at ~3.9%, so it crossed
    // `CAPTURE_GAP_MIN_SEC` every ~13 s of a perfectly healthy recording, padded half a
    // second, reset itself, and did it again. 326 recorded gaps across five handsets and
    // essentially all of them are that: 500-557 ms, pinned to the threshold, arriving on a
    // clock rather than on an event. Two things followed, and both were blamed on hardware.
    // Each padding shifts every later timestamp on that device by ~0.5 s, and the aligner
    // rejected devices by 656, 809, 959, 1261 and 1446 ms against a 400 ms limit -- one to
    // three paddings, exactly. And on the phone that tripped it 189 times in two sessions
    // it injected ~48 s of silence into a two-minute recording, so the transcriber answered
    // "no spoken audio" for every chunk of a conversation that had plainly happened.
    //
    // Deltas are what make the question answerable at all: a total cannot distinguish a
    // clock running slow from a microphone that stopped, because after enough time both
    // look like "we are short". `isCaptureStop` can, because over any single interval the
    // two look nothing alike.
    const wallDelta = wall - this.lastWall;
    const audioDelta = audioElapsed - this.lastAudio;
    this.lastWall = wall;
    this.lastAudio = audioElapsed;

    // **Compensate always; report only a real stop.** These were one decision and had to
    // stop being one.
    //
    // Compensating is not about the hole -- it is what keeps this buffer's clock on the
    // same footing as every other device's. §5 places devices with one scalar offset over
    // a ±2 s search, and an offset only exists if buffer time tracks wall time; a timeline
    // that runs 3.9% slow diverges linearly and no single offset ever fits it. So the
    // shortfall is padded whatever caused it, and `seconds` stays true.
    //
    // Reporting is a different question with a different answer. A `gap` is shown to the
    // participant, caveated in the result and counted as speech nobody is credited for, so
    // it has to mean the microphone actually stopped. Drift is not that, and saying so cost
    // a real session six caveats reading "Mohi's phone stopped recording" about a device
    // that recorded continuously.
    //
    // Doing it every tick rather than in half-second lumps is the other half. The same
    // 3.9% is absorbed either way, but a slice at a time keeps the timeline within ~20 ms
    // of the wall clock instead of sawtoothing ±500 ms across it -- and 20 ms of silence is
    // below a phoneme, where 500 ms swallows a word and a quarter of an ECAPA window.
    //
    // A second comparison runs beside the wall/context one: context advance against
    // audio actually *delivered* (the sample cursor, net of prior padding). The wall
    // comparison detects "context not running"; this one detects "context running
    // but capture dead" -- a throttled worklet or mic track with a live context,
    // where `currentTime` advances with the wall clock and the shortfall above reads
    // zero while the timeline silently concatenates across the hole. The delivery
    // deficit is ~0 under ordinary drift (cursor and context share a clock), so it
    // needs no drift allowance of its own; the same stop rule classifies it.
    //
    // A backlog that has not flushed yet looks exactly like a dead worklet for one
    // tick -- frames sit in the main-thread message queue while ECAPA embeds, and
    // even a main thread that has just freed up may not have drained the queue
    // before this tick's timer callback runs. So one suspicious tick only arms
    // suspicion, and padding and attestation wait for the *next* tick to still
    // show no delivery: by then the event loop has had every chance to flush,
    // and continued silence means the producer is dead rather than queued.
    // Padding over queued audio once cost a real session its transcript
    // (the 5.7 s fabrication `web_gap_probe.mjs` pins); this two-tick rule is
    // what keeps that fix fixed. There is deliberately no interval-length
    // shortcut: a throttled 60 s tick with a 5.7 s embed inside it is
    // observationally identical to a 60 s stall, and the late confirmation
    // still pads at the true hole -- the cursor has not moved since delivery
    // stopped, so appending then lands exactly where the audio stopped.
    const rate = this.timelineRate;
    const delivered = (this.length - this.paddedSamples) / rate;
    const deliveredDelta = delivered - this.lastDelivered;
    this.lastDelivered = delivered;
    const stall = audioDelta - deliveredDelta;
    // A confirmed stall is returned below in place of whatever the wall/context
    // accounting finds -- in practice the two never fire together (a frozen
    // context zeroes `audioDelta`, which zeroes the stall), but precedence
    // keeps one tick from ever reporting the same hole twice.
    let stallGap = null;
    if (isCaptureStop(stall, wallDelta)) {
      if (this.suspect !== null) {
        const since = this.suspect;
        const total = (audioElapsed - since.audio) - (delivered - since.delivered);
        this.suspect = null;
        const padFrames = Math.round(total * rate);
        if (padFrames >= 1) {
          const at = this.length / rate;
          this.chunks.push(new Float32Array(padFrames));
          this.length += padFrames;
          this.paddedSec += total;
          this.paddedSamples += padFrames;
          const round3 = (x) => Math.round(x * 1000) / 1000;
          stallGap = { start: round3(at), end: round3(at + total), cause: "delivery_stall" };
          this.gaps.push(stallGap);
        }
      } else {
        this.suspect = { audio: audioElapsed - audioDelta, delivered: delivered - deliveredDelta };
      }
    } else if (this.suspect !== null) {
      // Delivery resumed: the backlog flushed, the worklet was alive all along.
      this.suspect = null;
    }
    const missing = wallDelta - audioDelta;
    // Compensation belongs to the canonical timeline. It is inserted here, before
    // either batch or stream consumers can slice the recording, so it is not separately
    // resampled or phase-shifted by a consumer.
    const frames = Math.round(missing * rate);
    // Audio can briefly arrive ahead of the wall clock through buffering. That is a
    // surplus, not a hole, and there is nothing to insert for it.
    if (frames < 1) return stallGap;

    const at = this.length / rate;
    this.chunks.push(new Float32Array(frames));
    this.length += frames;
    this.paddedSec += missing;
    this.paddedSamples += frames;
    if (!isCaptureStop(missing, wallDelta)) return stallGap;

    const round3 = (x) => Math.round(x * 1000) / 1000;
    const gap = { start: round3(at), end: round3(at + missing), cause: "suspended" };
    this.gaps.push(gap);
    return stallGap ?? gap;
  }

  /** iOS suspends the context on interruption; resuming is worth attempting. */
  async resumeIfSuspended() {
    if (this.ctx && this.ctx.state === "suspended") {
      try {
        await this.ctx.resume();
      } catch {
        /* Nothing useful to do: `reconcile` reports the hole either way. */
      }
    }
    return this.ctx ? this.ctx.state : null;
  }

  /**
   * Rebuild capture onto the same canonical timeline after the context died.
   *
   * Suspension is the common case and `resumeIfSuspended` covers it; this is
   * for a context that is closed or gone, where resume is a no-op. Everything
   * identifying the recording survives -- chunks, cursor, wall anchor,
   * reported gaps, and critically `recording_started_at` upstream, so the
   * server keeps stitching one recorder generation instead of starting a
   * second one over the first. Only the clocks rebase, after one final audit:
   * `reconcile` runs first so the dead span is padded and attested rather
   * than forgiven by fresh baselines.
   *
   * Returns the new context state, or null when there was nothing to rebuild.
   */
  async restartCapture() {
    if (this.startedAtPerf === null) return null;
    if (this.ctx && this.ctx.state !== "closed") return this.ctx.state;
    this.reconcile();
    const tail = this.canonicalizer?.flush?.();
    if (tail?.length) {
      this.chunks.push(tail);
      this.length += tail.length;
    }
    try {
      if (this.node) this.node.disconnect();
    } catch {
      /* Already torn down. */
    }
    try {
      if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    } catch {
      /* Already ended. */
    }
    this.node = this.stream = this.ctx = null;
    this.stream = await openMic();
    try {
      this.ctx = new AudioContext({ sampleRate: K.SAMPLE_RATE });
    } catch {
      this.ctx = new AudioContext();
    }
    this.rate = this.ctx.sampleRate;
    this.canonicalizer = createCanonicalResampler(this.rate, this.timelineRate);
    await this.ctx.audioWorklet.addModule(new URL("./recorder-worklet.js", import.meta.url));
    const source = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, "pcm-recorder");
    this.node.port.onmessage = (e) => {
      this.workletMessages += 1;
      const canonical = this.canonicalizer.append(e.data);
      this.chunks.push(canonical);
      this.length += canonical.length;
      this.peak = framePeak(e.data, this.peak);
    };
    source.connect(this.node);
    const mute = this.ctx.createGain();
    mute.gain.value = 0;
    this.node.connect(mute).connect(this.ctx.destination);
    await this.ctx.resume();
    // Rebase both clocks onto the new context. The audit above already
    // accounted for everything up to this point, so these start clean.
    this.ctxStartTime = this.ctx.currentTime;
    this.lastWall = (performance.now() - this.startedAtPerf) / 1000;
    this.lastAudio = 0;
    this.lastDelivered = (this.length - this.paddedSamples) / this.timelineRate;
    this.suspect = null;
    this.capabilities = probeCapabilities(this.stream, this.ctx);
    return this.ctx.state;
  }

  /**
   * Elapsed recording time.
   *
   * Divides by the canonical timeline rate. The worklet may capture at another
   * `ctx.sampleRate`, but chunks are resampled once as they enter the recorder and
   * `slice()` never resamples a window independently. Cached state keeps the buffer
   * usable after `stop()` closes the context.
   */
  get seconds() {
    return this.timelineRate ? this.length / this.timelineRate : 0;
  }

  /** Number of samples currently available on the canonical timeline. */
  get sampleCount() {
    return this.length;
  }

  /**
   * Snapshot the monotonic capture clock beside the current logical sample
   * offset. This is diagnostic evidence only; batch and stream consumers keep
   * using the existing canonical timeline unchanged.
   */
  captureClockSample() {
    // These are deliberately adjacent reads. The pair is an anchor between the
    // monotonic audio timeline and the epoch clock used by the sync exchange;
    // reading them at different sites would add unmeasured elapsed time.
    const captureAnchorMonotonicMs = performance.now();
    const captureAnchorEpochMs = Date.now();
    return {
      capture_anchor_monotonic_ms: captureAnchorMonotonicMs,
      capture_anchor_epoch_ms: captureAnchorEpochMs,
      sample_offset: this.length,
      sample_rate_hz: this.timelineRate,
      // Include both timer resolution and one canonical sample's quantisation.
      uncertainty_ms: Math.max(CAPTURE_TIMER_RESOLUTION_MS, 1000 / this.timelineRate),
    };
  }

  /** Metadata-only capture health for diagnosing devices that emit no chunks. */
  captureHealth() {
    const tracks = this.stream?.getAudioTracks?.() ?? [];
    const track = tracks[0] ?? null;
    return {
      audio_context_state: this.ctx?.state ?? null,
      track_ready_state: track?.readyState ?? null,
      track_enabled: track?.enabled ?? null,
      sample_count: this.length,
      captured_sec: this.seconds,
      worklet_messages: this.workletMessages,
      sample_rate: this.rate || null,
      timeline_rate: this.timelineRate,
    };
  }

  async start() {
    this.workletMessages = 0;
    this.stream = await openMic();
    // Ask for a 16 kHz context so the existing fast path stays native. If the
    // browser declines, `createCanonicalResampler` handles the actual rate once
    // at the capture boundary below.
    try {
      this.ctx = new AudioContext({ sampleRate: K.SAMPLE_RATE });
    } catch {
      this.ctx = new AudioContext();
    }
    // Cached because `seconds` and `slice` must keep working after the context is
    // closed. A 16 kHz context is a request, not a guarantee, so this is whatever the
    // browser actually gave us.
    this.rate = this.ctx.sampleRate;
    this.timelineRate = K.SAMPLE_RATE;
    this.canonicalizer = createCanonicalResampler(this.rate, this.timelineRate);
    await this.ctx.audioWorklet.addModule(new URL("./recorder-worklet.js", import.meta.url));

    const source = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, "pcm-recorder");
    // Resolves on the first captured frame (#658). The meeting screen says "Setting up the
    // meeting…" until then and starts its clock here. It is a UI signal only: `startedAt`
    // below stays the capture start the server aligns on (#657), and is not moved by this.
    let heard;
    this.firstAudio = new Promise((resolve) => {
      heard = resolve;
    });
    this.node.port.onmessage = (e) => {
      this.workletMessages += 1;
      if (this.workletMessages === 1) heard();
      const canonical = this.canonicalizer.append(e.data);
      this.chunks.push(canonical);
      this.length += canonical.length;
      // Running peak for the level meter. A branch-and-compare over 128 samples, on a
      // handler that already runs 125 times a second; [M] draining a 13.6 s backlog of
      // these messages costs 7.5 ms in total, so this is not where the main thread goes.
      this.peak = framePeak(e.data, this.peak);
    };
    source.connect(this.node);
    // Worklets need a sink to be pulled; a zero-gain node keeps it running
    // without playing the mic back into the room.
    const mute = this.ctx.createGain();
    mute.gain.value = 0;
    this.node.connect(mute).connect(this.ctx.destination);

    // Explicit resume. Every other AudioContext in this codebase does it and this
    // one did not: a context created outside a user gesture starts `suspended` on
    // Safari, and a suspended context never runs the worklet -- silently, with
    // nothing thrown and no frames ever delivered.
    await this.ctx.resume();

    this.startedAt = new Date();
    this.startedAtPerf = performance.now();
    this.ctxStartTime = this.ctx.currentTime;
    this.paddedSec = 0;
    this.paddedSamples = 0;
    this.lastWall = 0;
    this.lastAudio = 0;
    this.lastDelivered = 0;
    this.suspect = null;
    this.capabilities = probeCapabilities(this.stream, this.ctx);
    return this.capabilities;
  }

  /** Samples in [fromSample, toSample), copied from the canonical timeline. */
  sliceSamples(fromSample, toSample) {
    const a = Math.max(0, Math.floor(fromSample));
    const b = Math.min(this.length, Math.floor(toSample));
    if (b <= a) return new Float32Array(0);

    const out = new Float32Array(b - a);
    let written = 0;
    let pos = 0;
    for (const chunk of this.chunks) {
      const start = Math.max(a, pos);
      const end = Math.min(b, pos + chunk.length);
      if (end > start) {
        out.set(chunk.subarray(start - pos, end - pos), written);
        written += end - start;
      }
      pos += chunk.length;
      if (pos >= b) break;
    }
    return out;
  }

  /** Samples in [fromSec, toSec), copied from the canonical 16 kHz timeline. */
  slice(fromSec, toSec) {
    return this.sliceSamples(fromSec * this.timelineRate, toSec * this.timelineRate);
  }

  async stop() {
    const tail = this.canonicalizer?.flush?.();
    if (tail?.length) {
      this.chunks.push(tail);
      this.length += tail.length;
    }
    if (this.node) this.node.disconnect();
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    if (this.ctx) await this.ctx.close();
    this.node = this.stream = this.ctx = null;
  }
}

/**
 * Stateful Butterworth low-pass. Keeping the section state lets the streaming
 * canonicalizer process worklet chunks without resetting filter boundaries.
 *
 * Phase distortion is acceptable here and linear phase is not worth its cost:
 * ECAPA sees log-mel magnitudes and the envelope is RMS, so both discard phase
 * entirely. Group delay at speech frequencies is well under a millisecond, which
 * is nothing against the 20 ms envelope frames alignment works on.
 */
function createLowPass(rate, cutoffHz, stages = 4) {
  const w = Math.tan((Math.PI * cutoffHz) / rate);
  const w2 = w * w;
  const sections = [];
  for (let s = 0; s < stages; s++) {
    // Butterworth pole angles, so the cascade is maximally flat in the passband.
    const q = 1 / (2 * Math.cos((Math.PI * (2 * s + 1)) / (4 * stages)));
    const norm = 1 / (1 + w / q + w2);
    sections.push({
      b0: w2 * norm,
      b1: 2 * w2 * norm,
      b2: w2 * norm,
      a1: 2 * (w2 - 1) * norm,
      a2: (1 - w / q + w2) * norm,
      z1: 0,
      z2: 0,
    });
  }
  return (input) => {
    let value = input;
    for (const section of sections) {
      const output = section.b0 * value + section.z1;
      section.z1 = section.b1 * value - section.a1 * output + section.z2;
      section.z2 = section.b2 * value - section.a2 * output;
      // `lowPass` writes each section's output through a Float32Array before the
      // next section reads it. Preserve that quantization in the streaming state.
      value = Math.fround(output);
    }
    return value;
  };
}

/** Butterworth low-pass, `stages` cascaded biquads. Mutates `x`. */
function lowPass(x, rate, cutoffHz, stages = 4) {
  const process = createLowPass(rate, cutoffHz, stages);
  for (let i = 0; i < x.length; i += 1) x[i] = process(x[i]);
}

/**
 * Create the one capture-boundary canonicalizer used by Recorder.
 *
 * Native samples are filtered and interpolated against a monotonically advancing
 * source phase. The phase and filter state survive every worklet message, so a 100 ms
 * stream frame and a later whole-session batch slice observe the same canonical sample
 * sequence. Compensation is appended by Recorder at this same canonical boundary.
 */
export function createCanonicalResampler(fromRate, toRate) {
  if (fromRate === toRate) {
    return { append: (input) => input, flush: () => new Float32Array(0) };
  }

  const ratio = fromRate / toRate;
  const process = toRate < fromRate ? createLowPass(fromRate, 0.45 * toRate) : null;
  let sourceIndex = -1;
  let nextOutput = 0;
  const history = [];
  const historyLimit = Math.max(2, Math.ceil(ratio) + 2);

  const sampleAt = (index) => {
    for (const sample of history) {
      if (sample.index === index) return sample.value;
    }
    return undefined;
  };

  return {
    append(input) {
      const output = [];
      for (let i = 0; i < input.length; i += 1) {
        const current = process ? process(input[i]) : input[i];
        sourceIndex += 1;
        history.push({ index: sourceIndex, value: current });
        if (history.length > historyLimit) history.shift();

        // Emit exactly the same floor(length / ratio) prefix as `resample`, while
        // looking up samples by absolute source index. The bounded history supplies
        // the interpolation pair without retaining the native recording wholesale.
        const count = Math.floor((sourceIndex + 1) / ratio);
        while (nextOutput < count) {
          const sourcePosition = nextOutput * ratio;
          const sourceBase = Math.floor(sourcePosition);
          // Interpolation needs the next source sample. End-of-input uses the
          // existing resample fallback (`b ?? a`) in `flush()` below.
          if (sourceBase + 1 > sourceIndex) break;
          const fraction = sourcePosition - sourceBase;
          const a = sampleAt(sourceBase) ?? 0;
          const b = sampleAt(sourceBase + 1) ?? a;
          output.push(a + (b - a) * fraction);
          nextOutput += 1;
        }
      }
      return Float32Array.from(output);
    },
    flush() {
      const output = [];
      const count = Math.floor((sourceIndex + 1) / ratio);
      while (nextOutput < count) {
        const sourcePosition = nextOutput * ratio;
        const sourceBase = Math.floor(sourcePosition);
        const fraction = sourcePosition - sourceBase;
        const a = sampleAt(sourceBase) ?? 0;
        const b = sampleAt(sourceBase + 1) ?? a;
        output.push(a + (b - a) * fraction);
        nextOutput += 1;
      }
      return Float32Array.from(output);
    },
  };
}

/**
 * Resample down to `toRate`. Only used when the browser refused a 16 kHz context.
 *
 * The low-pass is not optional politeness. Without it this was plain linear
 * interpolation, which does not remove anything above the new Nyquist -- it folds
 * it back down. Measured on a 48 kHz capture decimated to 16 kHz, the ultrasonic
 * join chirp at **18.0-19.4 kHz reappears at 2.0-3.4 kHz with its amplitude
 * completely intact**: an inaudible tone becomes a loud one sitting in the vowel
 * formants, in the recording the speaker embedding is computed from. Any content
 * above 8 kHz does the same thing; the chirp is simply the loudest example and the
 * one this app generates itself.
 */
export function resample(input, fromRate, toRate) {
  if (fromRate === toRate) return input;

  // Copy: the caller's buffer is a view into the recording, and `lowPass` mutates.
  const filtered = new Float32Array(input);
  if (toRate < fromRate) {
    // 0.45 rather than 0.5 leaves the transition band somewhere to live.
    lowPass(filtered, fromRate, 0.45 * toRate);
  }

  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(filtered.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const src = i * ratio;
    const j = Math.floor(src);
    const frac = src - j;
    const a = filtered[j] ?? 0;
    const b = filtered[j + 1] ?? a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

// -----------------------------------------------------------------------
// frame energy, VAD, envelope
// -----------------------------------------------------------------------

const FRAME = Math.round(K.VAD_FRAME_SEC * K.SAMPLE_RATE); // 400
const HOP = Math.round(K.VAD_HOP_SEC * K.SAMPLE_RATE); // 160

/** Per-frame RMS in dB, 25 ms frame / 10 ms hop. */
export function frameDb(wav) {
  if (wav.length < FRAME) return new Float32Array(0);
  const n = 1 + Math.floor((wav.length - FRAME) / HOP);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    const off = i * HOP;
    for (let j = 0; j < FRAME; j++) {
      const v = wav[off + j];
      sum += v * v;
    }
    out[i] = 20 * Math.log10(Math.sqrt(sum / FRAME) + 1e-10);
  }
  return out;
}

/** Running maximum over a window -- the local peak the VAD compares against. */
function maxFilter(x, size) {
  const out = new Float32Array(x.length);
  const half = Math.floor(size / 2);
  // Monotonic deque: O(n) rather than O(n*size), which matters on 11 minutes.
  const deque = [];
  let head = 0;
  for (let i = 0; i < x.length + half; i++) {
    if (i < x.length) {
      while (deque.length > head && x[deque[deque.length - 1]] <= x[i]) deque.pop();
      deque.push(i);
    }
    const centre = i - half;
    if (centre >= 0) {
      while (deque[head] < centre - half) head++;
      out[centre] = x[deque[head]];
    }
  }
  return out;
}

/**
 * Speech regions as [startSec, endSec].
 *
 * [M] The floor is local, not global: comparing each frame to the loudest frame
 * near it tracks whoever is currently talking. A global floor threw away 40% of
 * the quieter speaker when the other was 17.6 dB louder.
 */
export function speechRegions(wav) {
  const db = frameDb(wav);
  if (db.length === 0) return [];

  let globalMax = -Infinity;
  for (const v of db) if (v > globalMax) globalMax = v;
  const localPeak = maxFilter(db, Math.max(1, Math.round(K.VAD_LOCAL_WINDOW_SEC / K.VAD_HOP_SEC)));

  const gapFrames = Math.max(1, Math.round(K.VAD_BRIDGE_GAP_SEC / K.VAD_HOP_SEC));
  const regions = [];
  let start = null;
  let run = 0;
  for (let i = 0; i < db.length; i++) {
    const voiced =
      db[i] > localPeak[i] - K.VAD_LOCAL_TOP_DB && db[i] > globalMax - K.VAD_GLOBAL_FLOOR_DB;
    if (voiced) {
      if (start === null) start = i;
      run = 0;
    } else if (start !== null) {
      run++;
      if (run >= gapFrames) {
        regions.push([start, i - run]);
        start = null;
      }
    }
  }
  if (start !== null) regions.push([start, db.length]);

  const minFrames = K.VAD_MIN_REGION_SEC / K.VAD_HOP_SEC;
  return regions
    .filter(([a, b]) => b - a >= minFrames)
    .map(([a, b]) => [a * K.VAD_HOP_SEC, Math.min(b * K.VAD_HOP_SEC, wav.length / K.SAMPLE_RATE)]);
}

/** Trim leading and trailing silence, as enrollment does. */
export function trimSilence(wav) {
  const db = frameDb(wav);
  if (!db.length) return wav;
  let peak = -Infinity;
  for (const v of db) if (v > peak) peak = v;
  const floor = peak - K.TRIM_TOP_DB;
  let first = 0;
  let last = db.length - 1;
  while (first < db.length && db[first] < floor) first++;
  while (last > first && db[last] < floor) last--;
  return wav.subarray(Math.max(0, first * HOP), Math.min(wav.length, (last + 1) * HOP + FRAME));
}

/** Envelope at 50 Hz, max-pooled -- max keeps onsets, which is what alignment locks onto. */
export function wireEnvelope(wav) {
  const db = frameDb(wav);
  const factor = 1 / K.VAD_HOP_SEC / K.ENVELOPE_WIRE_RATE_HZ; // 100 Hz -> 50 Hz
  const n = Math.ceil(db.length / factor);
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const a = Math.floor(i * factor);
    const b = Math.min(db.length, Math.floor((i + 1) * factor));
    let m = -Infinity;
    for (let j = a; j < b; j++) if (db[j] > m) m = db[j];
    if (!Number.isFinite(m)) m = K.WIRE_DB_FLOOR;
    const clipped = Math.min(0, Math.max(K.WIRE_DB_FLOOR, m));
    out[i] = Math.round((clipped - K.WIRE_DB_FLOOR) / K.WIRE_DB_STEP);
  }
  return out;
}

export function bytesToBase64(bytes) {
  let s = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(s);
}
